/**
 * ID49 상품 수량별 차등 할인 (대량구매/공구·도매).
 *
 * be-catalog 계약(task #1): visibility ALL/B2B/HIDDEN, type AMOUNT/PERCENT, 7구간.
 * 수량 0(또는 빈값) 구간은 정책 연산에서 제외 → 저장 시 FE 가 0/빈 tier 를 걸러낸다.
 */
export const QUANTITY_DISCOUNT_VISIBILITIES = ['HIDDEN', 'B2B', 'ALL'] as const;
export type QuantityDiscountVisibility = (typeof QUANTITY_DISCOUNT_VISIBILITIES)[number];

export const QUANTITY_DISCOUNT_TYPES = ['AMOUNT', 'PERCENT'] as const;
export type QuantityDiscountType = (typeof QUANTITY_DISCOUNT_TYPES)[number];

/**
 * 단일 구간: minQty 개 이상 구매 시 discountValue(원 또는 %) 적용.
 * 필드명은 be-catalog 계약(product_quantity_discount_tier.min_qty/discount_value)과 정합.
 */
export interface QuantityDiscountTier {
  minQty: number;
  discountValue: number;
}

/** GET/PUT 본문 — BE 와 1:1. tiers 는 저장 시 minQty>0 만 포함(최대 7). */
export interface QuantityDiscountConfig {
  visibility: QuantityDiscountVisibility;
  discountType: QuantityDiscountType;
  tiers: QuantityDiscountTier[];
}

/** 화면 그리드 구간 수 (ID49_1.png 7개). */
export const QUANTITY_DISCOUNT_TIER_COUNT = 7;

/**
 * 그리드 입력(수량/할인 raw 문자열 배열)을 저장 페이로드 tiers 로 정규화.
 * ID49 규칙: 수량 0/빈값 구간은 제외, 할인은 0 이상만, minQty 오름차순 정렬.
 * 같은 입력은 같은 결과(순수 함수) — 단위테스트 대상.
 */
export const normalizeTiers = (
  rows: ReadonlyArray<{ minQty: string; discount: string }>,
): QuantityDiscountTier[] => {
  const toInt = (raw: string): number | null => {
    if (raw.trim() === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  };
  return rows
    .map((row) => ({ minQty: toInt(row.minQty), discountValue: toInt(row.discount) }))
    .filter(
      (tier): tier is QuantityDiscountTier =>
        tier.minQty != null &&
        tier.minQty > 0 &&
        tier.discountValue != null &&
        tier.discountValue >= 0,
    )
    .sort((a, b) => a.minQty - b.minQty);
};
